<script setup lang="ts">
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types.ts'
import { InventObjectTypePassportGrid } from '@/features/Inventory/ObjectTypePassportGrid'
import { InventTypeFieldSetting } from '@/entities/Inventory/TypeField'
import { InventObjectTypesGroupDelete } from '@/features/Inventory/ObjectTypesGroupDelete'
import { InventObjectTypesGroupModalButton } from '@/features/Inventory/ObjectTypesGroupModal'
import { useObjectTypesFieldsGroupsLayout } from '@/features/Inventory/ObjectTypes/model/useObjectTypesFieldsGroupsLayout'
import { useTranslate } from '@/shared/lib'
import { computed, type PropType, ref, watch } from 'vue'
import './assets/style.scss'
import InventObjectTypesFieldsGroupBody from './InventObjectTypesFieldsGroupBody.vue'

const TAB_GRID = 'grid'
const TAB_INFO = 'info'

const props = defineProps({
  groups: {
    type: Array as PropType<ObjectTypeGroup[]>,
    default: () => [],
  },
  refetch: {
    type: Function as PropType<() => void>,
    required: true,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  parentType: {
    type: Object as PropType<Record<string, unknown> | null>,
    default: null,
  },
  /** Корень с ровно одним ребёнком не может иметь собственных полей — кнопку прячем */
  hideAddField: {
    type: Boolean,
    default: false,
  },
})

const fields = defineModel<ObjectInventoryField[]>('fields', { default: [] })
const passportGridTemplate = defineModel<GridFieldType[]>('passportGridTemplate', { default: [] })

const { trs } = useTranslate()

const { groupsLayout, groupsView, displayGroupName } = useObjectTypesFieldsGroupsLayout(
  () => props.groups,
  fields,
  trs,
)

const openForm = ref(false)
const editField = ref<ObjectInventoryField | null>(null)

const editSettingsHandler = (field: ObjectInventoryField) => {
  openForm.value = true
  editField.value = field
}

const activeTab = ref<string>(TAB_GRID)
const contentReady = ref(true)

const tabKeys = computed(() => {
  const keys: string[] = [TAB_GRID]
  if (groupsLayout.value.primary) keys.push(TAB_INFO)
  for (const g of groupsLayout.value.tabGroups) {
    keys.push(String(g.id))
  }
  return keys
})

watch(
  tabKeys,
  (keys) => {
    if (!keys.includes(activeTab.value)) {
      activeTab.value = keys[0] ?? TAB_GRID
    }
  },
  { immediate: true },
)

const switchTab = (tab: string) => {
  if (tab === activeTab.value) return
  contentReady.value = false
  activeTab.value = tab
  setTimeout(() => {
    contentReady.value = true
  }, 400)
}
</script>

<template>
  <VTabs
    :model-value="activeTab"
    class="invent-object-types-form__unified-tabs mb-4"
    density="compact"
    show-arrows
  >
    <VTab :value="TAB_GRID" @click="switchTab(TAB_GRID)">{{ $t('itf.grid_settings') }}</VTab>
    <VTab v-if="groupsLayout.primary" :value="TAB_INFO" @click="switchTab(TAB_INFO)">{{ $t('itf.fields_settings') }}</VTab>
    <VTab
      v-for="g in groupsLayout.tabGroups"
      :key="g.id"
      :value="String(g.id)"
      :disabled="g.type === 'HISTORY'"
      @click="switchTab(String(g.id))"
    >
      {{ displayGroupName(g) }}
    </VTab>
  </VTabs>

  <div v-if="!contentReady" class="d-flex justify-center align-center" style="min-height: 300px">
    <VProgressCircular indeterminate color="primary" size="48" />
  </div>

  <template v-if="contentReady">
    <div v-show="activeTab === TAB_GRID">
      <InventObjectTypePassportGrid v-model="passportGridTemplate" :fields="fields" />
    </div>

    <div v-if="groupsLayout.primary" v-show="activeTab === TAB_INFO">
      <InventObjectTypesFieldsGroupBody
        :group="groupsLayout.primary"
        :fields="fields"
        :is-child="isChild"
        :parent-type="parentType"
        :hide-add-field="hideAddField"
        @edit-settings="editSettingsHandler"
      />
    </div>

    <template v-for="g in groupsLayout.tabGroups" :key="g.id">
      <div v-show="activeTab === String(g.id)">
        <div class="d-flex align-center flex-wrap justify-end gap-1 mb-4">
          <InventObjectTypesGroupModalButton v-if="g.mode !== 'PROTECTED' && !isChild" :group="g">
            <VBtn density="compact" icon>
              <VIcons type="settings" />
            </VBtn>
          </InventObjectTypesGroupModalButton>
          <InventObjectTypesGroupDelete
            v-if="g.mode === 'DEFAULT'"
            :group="g"
            @removed="() => refetch()"
          />
        </div>
        <InventObjectTypesFieldsGroupBody
          :group="g"
          :fields="fields"
          :is-child="isChild"
          :parent-type="parentType"
          :hide-add-field="hideAddField"
          @edit-settings="editSettingsHandler"
        />
      </div>
    </template>
  </template>

  <InventTypeFieldSetting
    v-if="editField"
    :groups="groupsView"
    v-model="openForm"
    v-model:field="editField"
  />
</template>
